import { defineStore } from "pinia";
import { useRuntimeConfig } from "#imports";
import { useKratos } from "~/composables/auth/useKratos";
import { logger } from "~/utils/logger";

export interface AuthUser {
  id: string;
  email: string;
  name?: string;
  role?: string;
  image?: string | null;
}

const LOCAL_KEY = "tayba:auth";
// local users storage removed; user management is backend-only

export const ROLE_USER = "USER";
export const ROLE_ADMIN = "ADMIN";
export const ROLE_BOOKING_MANAGER = "BOOKING_MANAGER";

export const useAuthStore = defineStore("auth", {
  state: () => {
    // Don't use useState for initial state - causes hydration mismatch
    // The middleware will restore session after page loads
    return {
      user: null as AuthUser | null,
      loading: false,
      // whether we've attempted to restore session from httpOnly cookies
      sessionRestored: false,
      // flag to track if email verification is required
      verificationRequired: false,
    };
  },
  getters: {
    isAuthenticated: (s) => !!s.user,
    isAdmin: (s) =>
      (s.user?.role || "").toString().trim().toUpperCase() === ROLE_ADMIN,
  },
  actions: {
    // Load auth from localStorage (client-only)
    loadFromStorage() {
      // No-op: persistent client-side storage removed in backend-only mode
      return null;
    },

    // Persist current auth state to localStorage
    saveToStorage() {
      // No-op: do not persist to localStorage in backend-only mode
      return;
    },

    // Client-only registration: store provided user/token locally
    async register(input: { name: string; email: string; password: string }) {
      const cfg = useRuntimeConfig();
      const pub = (cfg.public || {}) as any;
      if (!pub.useBackend) {
        throw new Error(
          "Auth requires backend mode (NUXT_PUBLIC_USE_BACKEND=true)",
        );
      }
      const base = String(pub.apiBase || "");
      const res: any = await $fetch(
        `${base.replace(/\/$/, "")}/auth/register`,
        {
          method: "POST",
          body: {
            name: input.name,
            email: input.email,
            password: input.password,
          },
          credentials: "include",
        },
      );
      this.user = res.user || null;
      // Mark session as restored so middleware knows user is authenticated
      this.sessionRestored = true;
      return this.user;
    },

    // Login via backend
    async login(input: { email: string; password: string }) {
      const cfg = useRuntimeConfig();
      const pub = (cfg.public || {}) as any;
      if (!pub.useBackend) {
        throw new Error(
          "Auth requires backend mode (NUXT_PUBLIC_USE_BACKEND=true)",
        );
      }
      const base = String(pub.apiBase || "");
      const res: any = await $fetch(`${base.replace(/\/$/, "")}/auth/login`, {
        method: "POST",
        body: { email: input.email, password: input.password },
        credentials: "include",
      });
      this.user = res.user || null;
      this.saveToStorage();
      return this.user;
    },

    // Google login via backend
    async loginWithGoogle(idToken: string) {
      const cfg = useRuntimeConfig();
      const pub = (cfg.public || {}) as any;
      if (!pub.useBackend)
        throw new Error("Google login requires backend mode");
      const base = String(pub.apiBase || "");
      const res: any = await $fetch(
        `${base.replace(/\/$/, "")}/auth/login-google`,
        {
          method: "POST",
          body: { idToken },
          credentials: "include",
        },
      );
      this.user = res.user || null;
      this.saveToStorage();
      return this.user;
    },

    // Update profile (backend)
    async updateProfile(input: { name?: string; email?: string }) {
      if (!this.user) return null;
      const cfg = useRuntimeConfig();
      const pub = (cfg.public || {}) as any;
      if (!pub.useBackend)
        throw new Error("Profile update requires backend mode");
      const base = String(pub.apiBase || "");
      // Update the current authenticated user's profile via /users/me (Kratos)
      const res: any = await $fetch(`${base.replace(/\/$/, "")}/users/me`, {
        method: "PATCH",
        body: input,
        credentials: "include",
      });
      this.user = res.user || this.user;
      this.saveToStorage();
      return this.user;
    },

    // Change password (backend)
    async changePassword(currentPassword: string, newPassword: string) {
      if (!this.user) return false;
      const cfg = useRuntimeConfig();
      const pub = (cfg.public || {}) as any;
      if (!pub.useBackend)
        throw new Error("Change password requires backend mode");
      const base = String(pub.apiBase || "");
      await $fetch(`${base.replace(/\/$/, "")}/users/change-password`, {
        method: "POST",
        body: { currentPassword, newPassword },
        credentials: "include",
      });
      return true;
    },

    // Ensure we've attempted to restore a session once. Useful for middleware
    // to avoid redirecting before the background plugin finishes rehydration.
    async ensureSessionRestored() {
      if (this.sessionRestored) return;
      // restoreSession will mark sessionRestored when finished
      await this.restoreSession();
    },

    // Attempt to restore session on page load using Kratos session cookie.
    // Checks Kratos session and syncs user from backend.
    async restoreSession() {
      const cfg = useRuntimeConfig();
      const pub = (cfg.public || {}) as any;
      if (!pub.useBackend) return this.loadFromStorage();

      try {
        // Check Kratos session first
        const kratos = useKratos();
        const session = await kratos.getSession();

        if (!session) {
          this.user = null;
          return null;
        }

        // Session exists, fetch user details from backend
        const base = String(pub.apiBase || "");
        const res: any = await $fetch(`${base.replace(/\/$/, "")}/users/me`, {
          method: "GET",
          credentials: "include",
        });
        this.user = res?.user || null;
        return this.user;
      } catch {
        this.user = null as any;
        return null;
      } finally {
        this.sessionRestored = true;
      }
    },

    // Check current Kratos session and update user state
    // If throwOnUnverified is true, will throw error for unverified emails
    // Otherwise, will just return null and clear user state
    async checkKratosSession(throwOnUnverified: boolean = false) {
      // If we have a valid user already, skip check unless explicitly requested
      if (this.user && this.sessionRestored && !throwOnUnverified) {
        return this.user;
      }

      try {
        const kratos = useKratos();
        const session = await kratos.getSession();

        if (!session) {
          this.user = null;
          this.sessionRestored = true;
          return null;
        }

        // If we have a session, sync with backend
        const cfg = useRuntimeConfig();
        const pub = (cfg.public || {}) as any;
        const base = String(pub.apiBase || "");

        const res: any = await $fetch(`${base.replace(/\/$/, "")}/users/me`, {
          method: "GET",
          credentials: "include",
        });

        this.user = res?.user || null;
        this.sessionRestored = true;
        return this.user;
      } catch (error: any) {
        // Check if this is an email verification required error
        const status =
          error?.status || error?.statusCode || error?.response?.status;
        const errorData = error?.data || error?.response?.data || {};
        const errorCode = errorData?.error || errorData?.statusCode;
        const errorMessage = errorData?.message || error?.message || "";

        if (
          status === 403 ||
          errorCode === "EMAIL_VERIFICATION_REQUIRED" ||
          errorMessage.includes("Email verification required")
        ) {
          // User has a session but email is not verified
          this.user = null;
          this.verificationRequired = true;

          if (throwOnUnverified) {
            throw new Error("EMAIL_VERIFICATION_REQUIRED");
          }

          return null;
        }

        // Clear user state on error
        this.user = null;
        this.sessionRestored = true;
        this.verificationRequired = false;
        return null;
      }
    },

    async logout() {
      // Clear local state immediately
      this.user = null as any;
      this.sessionRestored = false;
      this.verificationRequired = false;

      // Clear booking state
      if (typeof window !== "undefined") {
        try {
          const { useBookingStore } = await import("~/stores/booking");
          const bookingStore = useBookingStore();
          bookingStore.resetBooking();
        } catch {}

        // Clear all storage
        try {
          localStorage.clear();
          sessionStorage.clear();
        } catch {}

        // Check if current page requires auth and redirect to home if needed
        try {
          const router = useRouter();
          const currentRoute = router.currentRoute.value;
          const path = currentRoute.path;

          // Redirect if on protected routes
          const isProtectedRoute =
            currentRoute.meta?.requiresAuth ||
            path.startsWith("/admin") ||
            path.startsWith("/booking-manager") ||
            path.startsWith("/dashboard");

          if (isProtectedRoute) {
            await navigateTo("/");
          }
        } catch (e) {
          logger.error("Error checking route during logout:", e);
        }
      }

      // Call Kratos logout in background (don't wait for it)
      const kratos = useKratos();
      kratos.logout().catch(() => {
        // Ignore errors, we're already logging out
      });
    },

    // --- Users management (backend-only) ---
    // Ensure there's an initial admin available for admin sign-in pages.
    // In backend mode we assume the backend seed provides an admin and do nothing.
    // In local/mock mode we create a default admin in localStorage so admin pages work.
    // no-op placeholder kept so callers remain valid; backend seed provides admin
    ensureInitialAdmin() {
      return;
    },

    // Login using a local users store or forward to backend login when in backend mode.
    // Returns the user object or null on failure.
    async loginWithLocalUser(email: string, password: string) {
      return await this.login({ email, password });
    },

    async loadUsers(): Promise<Array<AuthUser & { password?: string }>> {
      const cfg = useRuntimeConfig();
      const pub = (cfg.public || {}) as any;
      if (!pub.useBackend)
        throw new Error("User management requires backend mode");
      const base = String(pub.apiBase || "");
      const res: any = await $fetch(
        `${base.replace(/\/$/, "")}/users?page=1&limit=1000`,
        {
          credentials: "include",
        },
      );
      return (res?.users || []).map((u: any) => ({
        id: u.id,
        email: (u.email || "").toString().trim().toLowerCase(),
        name: u.name,
        role: (u.role || ROLE_USER).toString().trim().toUpperCase(),
        image: u.image || null,
      }));
    },

    saveUsers(_: Array<AuthUser & { password?: string }>) {
      // Not supported in backend-only mode
      throw new Error(
        "saveUsers is not supported when backend mode is enabled",
      );
    },

    // Admin-only: create user (backend)
    async createUser(input: {
      name: string;
      email: string;
      password: string;
      role?: string;
    }) {
      if (
        !this.user ||
        ((this.user.role || "") as string).toString().trim().toUpperCase() !==
          ROLE_ADMIN
      ) {
        throw new Error("Unauthorized: only admin can create users");
      }
      const cfg = useRuntimeConfig();
      const pub = (cfg.public || {}) as any;
      if (!pub.useBackend) throw new Error("createUser requires backend mode");
      const base = String(pub.apiBase || "");
      const res: any = await $fetch(`${base.replace(/\/$/, "")}/users`, {
        method: "POST",
        body: input,
        credentials: "include",
      });
      return res.user || res;
    },

    // Admin-only: update user (backend)
    async updateUser(
      userId: string,
      updates: {
        name?: string;
        email?: string;
        role?: string;
        disabled?: boolean;
      },
    ) {
      if (
        !this.user ||
        ((this.user.role || "") as string).toString().trim().toUpperCase() !==
          ROLE_ADMIN
      ) {
        throw new Error("Unauthorized: only admin can update users");
      }
      const cfg = useRuntimeConfig();
      const pub = (cfg.public || {}) as any;
      if (!pub.useBackend) throw new Error("updateUser requires backend mode");
      const base = String(pub.apiBase || "");
      const res: any = await $fetch(
        `${base.replace(/\/$/, "")}/users/${encodeURIComponent(userId)}`,
        {
          method: "PATCH",
          body: updates,
          credentials: "include",
        },
      );
      return res.user || res;
    },

    // Admin-only: delete user (backend)
    async deleteUser(userId: string) {
      if (
        !this.user ||
        ((this.user.role || "") as string).toString().trim().toUpperCase() !==
          ROLE_ADMIN
      ) {
        throw new Error("Unauthorized: only admin can delete users");
      }
      const cfg = useRuntimeConfig();
      const pub = (cfg.public || {}) as any;
      if (!pub.useBackend) throw new Error("deleteUser requires backend mode");
      const base = String(pub.apiBase || "");
      await $fetch(
        `${base.replace(/\/$/, "")}/users/${encodeURIComponent(userId)}`,
        {
          method: "DELETE",
          credentials: "include",
        },
      );
      return true;
    },
  },
  // No client-side persistence; tokens are stored only in-memory and via backend session flows
});
